/*
 * NOTE: These functions were copied from lightyear-data to eliminate the need to include lightyear-data as a dependency.
 * They are also included in shared-image-viewer, if these functions change, the ones in shared-image-viewer should change as well.
 */

import { i18n } from '@fs/zion-locale'
import Internationalization from 'intl'

const EMDASH = '—'

/**
 * Formats a date into the given format, for the given locale
 * @param {Date} date Date object to format
 * @param {String} format Name of the format to format the string
 * @param {String} [locale='en'] Locale in which to format the date
 */
function formatDate({ date, format = 'COMPACT', locale = 'en' } = {}) {
  // This function is isomorphic. Browser implements Intl by default.
  const intl = typeof window === 'undefined' ? Internationalization : Intl

  if (date) {
    let dateFormat

    switch (format) {
      case 'COMPACT':
        dateFormat = new intl.DateTimeFormat(locale, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
        break
      case 'MINIMAL':
        dateFormat = new intl.DateTimeFormat(locale, {
          month: 'short',
          year: 'numeric',
        })
        break
      case 'FULL_WITH_TIME':
        dateFormat = new intl.DateTimeFormat(locale, {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
          hour12: true,
        })
        break
      case 'COMPACT_WITH_TIME':
        dateFormat = new intl.DateTimeFormat(locale, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: 'numeric',
          hour12: true,
        })
        break
      case 'COMPACT_WITH_HOUR_MINUTE':
        dateFormat = new intl.DateTimeFormat(locale, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        })
        break
      case 'MINIMAL_WITH_TIME':
        dateFormat = new intl.DateTimeFormat(locale, {
          hour: 'numeric',
          minute: 'numeric',
          hour12: true,
        })
        break
      case 'MONTH_YEAR':
        dateFormat = new intl.DateTimeFormat(locale, {
          month: 'short',
          year: 'numeric',
        })
        break
      case 'YEAR':
        dateFormat = new intl.DateTimeFormat(locale, {
          year: 'numeric',
        })
        break
      default:
        throw new Error(`Unknown formatType:${format}`)
    }

    return dateFormat.format(date)
  }

  return null
}

export function formattedYearRange(years) {
  return [
    ...new Set(
      years
        .filter(Boolean)
        .map((year) =>
          year.toString().startsWith('-')
            ? i18n.t('shared.bcYear', '{year} BC', { year: year.toString().substr(1) })
            : year
        )
    ),
  ].join(EMDASH)
}

/**
 * Formats one or two ISO date strings according to rules that have been established over time
 * RULES:
 *    1-) If the beginning date start with 1 January and the ending date is 31 December, then just show the years. If the years are the same, then just show one year
 *    2-) If the beginning date is the start of a month and the ending date is the end of a month then show the month and year
 *    3-) If the dates are the same, then just show the single date
 *    4-) Otherwise show the full day, month and year of both the start and ending date
 * @param {String} fromISOString Beginning date string in ISO format
 * @param {String} toISOString Ending date string in ISO format
 * @param {String} locale Locale in which to format the date(s)
 */
export function formatWithRules({ fromISOString, toISOString, locale = 'en' } = {}) {
  // Dates coming from coverages will always have this format: '1819-03-31T00:00:00.000+0000'
  const regex = /(\d{4})-(\d{2})-(\d{2})/
  const [, fromYear, fromMonth, fromDay] = fromISOString && fromISOString.match(regex) ? fromISOString.match(regex) : []
  const [, toYear, toMonth, toDay] = toISOString && toISOString.match(regex) ? toISOString.match(regex) : []

  const fromDate =
    fromISOString && fromISOString.match(regex)
      ? new Date(new Date().setFullYear(fromYear, fromMonth - 1, fromDay))
      : null
  let toDate =
    toISOString && toISOString.match(regex) ? new Date(new Date().setFullYear(toYear, toMonth - 1, toDay)) : null
  let format = 'COMPACT'

  // Rule #1
  if (fromDay === '01' && fromMonth === '01' && toDay === '31' && toMonth === '12') {
    format = 'YEAR'
    if (fromYear === toYear) {
      toDate = null
    }

    // Rule #2
  } else if (fromDay === '01' && isLastDayOfMonth(toYear, Number(toMonth) - 1, toDay)) {
    // If the years are different then show both years
    format = 'MONTH_YEAR'
    if (fromYear === toYear && fromMonth === toMonth) {
      toDate = null
    }

    // Rule #3
  } else if (fromYear === toYear && fromMonth === toMonth && fromDay === toDay) {
    format = 'COMPACT'
    toDate = null

    // Rule #4
  } else {
    format = 'COMPACT'
  }

  return joinDates({ fromDate, toDate, format, locale })
}

export function getCoverageDatesFromGedcomX(gedcomX) {
  let fromDate
  let toDate

  if (isValidGedcomXDate(gedcomX)) {
    // splits the date into "from" and "to" and removes the "+" the gedcomX has for AD
    if (gedcomX.includes('/')) [fromDate, toDate] = gedcomX.split('/').map((date) => date.replace('+', ''))
    else {
      const cleanedYear = gedcomX.replace('+', '')
      fromDate = `${cleanedYear}-01-01`
      toDate = `${cleanedYear}-12-31`
    }

    fromDate = fromDate ? getSplitDate(fromDate) : null
    toDate = toDate ? getSplitDate(toDate) : null

    if (fromDate) {
      // Default fromDate day and month to '01'
      fromDate.month = fromDate.month || '01'
      fromDate.day = fromDate.day || '01'
    }

    // If there is toYear and toMonth but no toDay, use the last day of the month
    if (toDate?.year && toDate?.month && !toDate?.day) {
      const numDays = getNumDaysInMonth(Number(toDate.month) - 1, toDate.year)
      if (numDays) {
        toDate.day = numDays.toString().padStart(2, '0')
      }

      // If there is toYear but no toMonth or toDay, set it as Dec. 31st
    } else if (toDate?.year && !toDate?.month && !toDate?.day) {
      toDate.month = '12'
      toDate.day = '31'
    }
  }

  const fromDateFormatted = fromDate
    ? `${fromDate.isBC ? '-' : ''}${fromDate.year}-${fromDate.month}-${fromDate.day}`
    : null
  const toDateFormatted = toDate ? `${toDate.isBC ? '-' : ''}${toDate.year}-${toDate.month}-${toDate.day}` : null

  return [fromDateFormatted, toDateFormatted]
}

function getNumDaysInMonth(month, year) {
  if (Number(month) < 0 || Number(month) > 11) {
    return null
  }
  if (!year || Number.isNaN(Number(year))) {
    year = new Date().getFullYear()
  }
  const numDays = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  return numDays[Number(month)]
}

function getSplitDate(date) {
  if (date) {
    const isBC = date.startsWith('-')
    // eslint-disable-next-line prefer-const
    let [year, month, day] = (isBC ? (date.substring(1) || '').split('-') : date.split('-')) || []

    // Format as a 6 digit year so BC dates can work
    if (isBC && year) {
      year = year.padStart(6, '0')
    }

    return { isBC, year, month, day }
  }
  return {}
}

/**
 * Returns true if the date is the last day of the month
 * @param  {String|Number}  year  Year of the date
 * @param  {String|Number}  month Month of the date (e.g. 0 - January, 1 - February, etc...)
 * @param  {String|Number}  day   Day of the date
 * @return {Boolean} Returns true if the date is the last day of the month
 */
function isLastDayOfMonth(year, month, day) {
  return getNumDaysInMonth(month, year) === Number(day)
}

function isLeapYear(year) {
  const yearNum = Number(year)
  return !Number.isNaN(yearNum) && ((yearNum % 4 === 0 && yearNum % 100 !== 0) || yearNum % 400 === 0)
}

function isValidGedcomXDate(gedcomX) {
  if (!gedcomX || typeof gedcomX !== 'string') {
    return false
  }
  const regExp = /^\/?(\+|-)\d{4,6}(-\d{2}(-\d{2})?)?\/?((\+|-)\d{4,6}(-\d{2}(-\d{2})?)?)?/i
  const matches = gedcomX.match(regExp)
  const firstMatch = Array.isArray(matches) ? matches[0] : ''
  return firstMatch ? firstMatch.length === gedcomX.length : false
}

/**
 * Helper method join two dates together if they are both present
 * @param {Object} fromDate Beginning date
 * @param {Object} toDate Ending date
 * @param {String} format Name of the format in which to do the formatting
 * @param {String} locale Locale in which to format the dates
 */
export function joinDates({ fromDate, toDate, format, locale = 'en' }) {
  const formattedFromDate = fromDate ? formatDate({ date: fromDate, format, locale }) : null
  const formattedToDate = toDate ? formatDate({ date: toDate, format, locale }) : null

  if (formattedFromDate && formattedToDate) {
    return `${formattedFromDate}${EMDASH}${formattedToDate}`
  }

  return formattedFromDate || formattedToDate
}
